import { ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { SolutionsAppService } from 'src/app/services/solutions-app.service';
import { SharedFeaturesService } from 'src/app/services/shared-features.service';
import { SolutionExecutorFormComponent } from 'src/app/modules/sales-dna/components/solution-executor-form/solution-executor-form.component';
import { AddUserInSubscribedSolnComponent } from '../add-user-in-subscribed-soln/add-user-in-subscribed-soln.component';
import { RunProjectsDialogComponent } from '../run-projects-dialog/run-projects-dialog.component';

/** One subscription, as shown on both customer Home and My Solutions. */
@Component({
  selector: 'app-subscription-card',
  template: `
    <div class="sub-card">
      <div class="sub-head">
        <div class="sub-ico">
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"
               stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <rect x="3" y="4" width="18" height="13" rx="2" /><path d="M8 21h8M12 17v4" />
          </svg>
        </div>
        <div class="sub-mid">
          <div class="sub-nm">
            <button type="button" class="lnk" matTooltip="Open the configuration form" (click)="configure()">
              {{ sub['solution_name'] | textpretty | titlecase }}
            </button>
            <span class="vchip" *ngIf="sub['version_name']">{{ sub['version_name'] }}</span>
          </div>
          <div class="sub-meta">{{ subMeta() }}</div>
        </div>
        <div class="sub-actions">
          <button type="button" class="btn-ghost" (click)="addUsers()">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
                 stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <circle cx="9" cy="8" r="3" /><path d="M2 21c0-3.5 3-6 7-6s7 2.5 7 6" />
              <path d="M17 8a3 3 0 0 1 0 6M22 21c0-2.5-1.3-4.4-3.5-5.3" />
            </svg>
            Manage users
          </button>
          <button type="button" class="btn-ghost" (click)="configure()">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
                 stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="3" />
              <path d="M19 12a7 7 0 0 0-.1-1.2l2-1.6-2-3.4-2.4 1a7 7 0 0 0-2-1.2L14 2h-4l-.5 2.6a7 7 0 0 0-2 1.2l-2.4-1-2 3.4 2 1.6A7 7 0 0 0 5 12a7 7 0 0 0 .1 1.2l-2 1.6 2 3.4 2.4-1a7 7 0 0 0 2 1.2L10 22h4l.5-2.6a7 7 0 0 0 2-1.2l2.4 1 2-3.4-2-1.6A7 7 0 0 0 19 12Z" />
            </svg>
            Configure
          </button>
          <button type="button" class="btn-primary" [disabled]="runDialogOpen" (click)="openRunDialog()">
            <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" stroke="none" aria-hidden="true">
              <path d="M8 5v14l11-7z" />
            </svg>
            Run
          </button>
          <button
            *ngIf="expandable"
            type="button"
            class="expander"
            [attr.aria-expanded]="expanded"
            [attr.aria-label]="expanded ? 'Hide projects' : 'Show projects'"
            [matTooltip]="expanded ? 'Hide projects' : 'Show projects'"
            (click)="expanded = !expanded"
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor"
                 stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
        </div>
      </div>

      <div class="proj-wrap" *ngIf="expandable && expanded">
        <div class="proj-lbl">Projects in this solution</div>
        <div class="proj-empty" *ngIf="projectsLoaded && !projects.length">No projects in this solution.</div>
        <div class="proj-row" *ngFor="let project of projects">
          <span class="proj-ico">
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"
                 stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M4 7h10M4 12h16M4 17h7" />
            </svg>
          </span>
          <span class="proj-nm" [matTooltip]="project['project_name']">{{
            project['project_name'] | namingsize : 50
          }}</span>
          <button
            type="button"
            class="btn-ghost"
            [disabled]="runningProjects[project['project_id']]"
            (click)="runProject(project)"
          >
            <svg viewBox="0 0 24 24" width="15" height="15" fill="currentColor" stroke="none" aria-hidden="true">
              <path d="M8 5v14l11-7z" />
            </svg>
            {{ runningProjects[project['project_id']] ? 'Queuing…' : 'Run' }}
          </button>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./subscription-card.component.scss'],
})
export class SubscriptionCardComponent implements OnInit {
  @Input() sub: any;
  /** Shows the chevron and the per-project Run rows (My Solutions). */
  @Input() expandable = false;
  @Input() startExpanded = false;

  expanded = false;
  projects: any[] = [];
  projectsLoaded = false;
  runningProjects: { [projectId: string]: boolean } = {};
  runDialogOpen = false;

  constructor(
    private solutionServices: SolutionsAppService,
    private snackBar: SharedFeaturesService,
    private dialog: MatDialog,
    private router: Router,
    private detectChange: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.expanded = this.startExpanded;
    if (this.expandable) {
      this.loadProjects();
    }
  }

  private loadProjects(): void {
    this.solutionServices.getAvailableProjects(this.sub, this.sub, '').subscribe({
      next: (response: any) => {
        this.projects = RunProjectsDialogComponent.inRunOrder(response || []);
        this.projectsLoaded = true;
      },
      error: (error: any) => {
        console.error('an error occurred loading projects', JSON.stringify(error));
        this.projectsLoaded = true;
      },
    });
  }

  subMeta(): string {
    const fmt = (v: string) =>
      new Date(v).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    const parts: string[] = [];
    if (this.sub?.started_on_dtm) parts.push('Subscribed ' + fmt(this.sub.started_on_dtm));
    if (this.sub?.no_of_users) {
      parts.push(`${this.sub.no_of_users} user${this.sub.no_of_users === 1 ? '' : 's'}`);
    }
    if (this.sub?.expires_on_dtm) parts.push('renews ' + fmt(this.sub.expires_on_dtm));
    return parts.join(' · ');
  }

  addUsers(): void {
    const ref = this.dialog.open(AddUserInSubscribedSolnComponent, {
      data: this.sub,
      width: '600px',
      maxWidth: '92vw',
      panelClass: 'manage-users-dialog',
      disableClose: false,
    });
    ref.afterClosed().subscribe(() => this.detectChange.detectChanges());
  }

  configure(): void {
    this.solutionServices
      .getForm({ solution_id: this.sub['solution_id'] }, { version_id: this.sub['version_id'] })
      .subscribe({
        next: (response: any) => {
          const data =
            response.length === 0
              ? undefined
              : { subscriptionDetails: this.sub, formData: response[0], preview: false };
          const ref = this.dialog.open(SolutionExecutorFormComponent, {
            data,
            panelClass: 'executor-form-dialog',
            disableClose: false,
          });
          ref.afterClosed().subscribe(() => this.detectChange.detectChanges());
        },
        error: () => {
          this.router.navigate(['/home/customer/manage-solutions']);
        },
      });
  }

  openRunDialog(): void {
    if (this.runDialogOpen) return;
    this.runDialogOpen = true;
    const ref = this.dialog.open(RunProjectsDialogComponent, {
      data: { sub: this.sub },
      width: '560px',
      maxWidth: '94vw',
      panelClass: 'run-projects-dialog',
      autoFocus: false,
    });
    ref.afterClosed().subscribe((taskId?: string) => {
      this.runDialogOpen = false;
      if (taskId !== undefined) {
        this.snackBar.openSnackBar(
          `Run queued${taskId ? ` (task #${String(taskId).slice(0, 8)})` : ''}. We'll let you know when it finishes.`,
          'Close',
          'success-snackbar'
        );
      }
      this.detectChange.detectChanges();
    });
  }

  runProject(project: any): void {
    const projectId = project['project_id'];
    const projectName = project['project_name'];
    if (this.runningProjects[projectId]) return;
    this.runningProjects[projectId] = true;
    this.solutionServices
      .runVersionOrProjectOrPipelineOrNode(this.sub?.solution_id, this.sub?.version_id, {
        project_ids: [projectId],
        pipeline_ids: [],
        node_ids: [],
        tags: [],
      })
      .subscribe({
        next: (response: any) => {
          this.runningProjects[projectId] = false;
          this.snackBar.openSnackBar(
            `"${projectName}" queued${response?.task_id ? ` (task #${String(response.task_id).slice(0, 8)})` : ''}. We'll let you know when it finishes.`,
            'Close',
            'success-snackbar'
          );
        },
        error: () => {
          this.runningProjects[projectId] = false;
          this.snackBar.openSnackBar('Unable to run project', 'Close', 'error-snackbar');
        },
      });
  }
}
